import type { FranchisePlayer, FranchisePlayerPosition } from './player-model';

export const PLAYER_PLAY_STYLE_DEFINITIONS = {
  'pocket-passer': ['Pocket Passer', 'Wins from structure with timing and placement'],
  gunslinger: ['Gunslinger', 'Attacks tight windows and pushes the ball downfield'],
  'field-general': ['Field General', 'Diagnoses coverage and distributes efficiently'],
  mobile: ['Mobile', 'Creates designed and improvised rushing value'],
  improviser: ['Improviser', 'Extends plays and attacks outside structure'],
  'bell-cow': ['Bell Cow', 'Handles a large, varied rushing workload'],
  'power-back': ['Power Back', 'Excels inside and in short-yardage situations'],
  'scat-back': ['Scat Back', 'Threatens the perimeter and open field'],
  'receiving-back': ['Mismatch Back', 'Commands routes, checkdowns, and screens'],
  'blocking-back': ['Protector Back', 'Prioritizes protection and lead blocking'],
  'short-yardage-back': ['Short-Yardage Back', 'Specializes in compact, high-leverage carries'],
  'route-technician': ['Route Technician', 'Separates with precision at every level'],
  'vertical-threat': ['Vertical Threat', 'Stretches coverage and wins downfield'],
  'slot-separator': ['Slot Separator', 'Finds leverage quickly between defenders'],
  'possession-receiver': ['Possession Receiver', 'Wins reliable chain-moving targets'],
  'yac-creator': ['YAC Creator', 'Turns short completions into open-field gains'],
  'receiving-tight-end': ['Receiving TE', 'Functions as a featured receiving weapon'],
  'inline-blocker': ['Inline Blocker', 'Adds value at the point of attack and in protection'],
  'seam-threat': ['Seam Threat', 'Attacks linebackers and safeties vertically'],
  'safety-valve': ['Safety Valve', 'Provides dependable underneath answers'],
  'h-back': ['H-Back', 'Moves across the formation as a blocker and receiver'],
  anchor: ['Anchor', 'Absorbs power and stabilizes the pocket'],
  mauler: ['Mauler', 'Creates movement in the run game'],
  technician: ['Technician', 'Wins with footwork, leverage, and assignment discipline'],
  'athletic-protector': ['Athletic Protector', 'Handles space and speed rushers'],
  'pulling-specialist': ['Pulling Specialist', 'Excels leading runs in space'],
  'line-caller': ['Line Caller', 'Raises cohesion through recognition and communication'],
  'zone-mover': ['Zone Mover', 'Uses reach, angles, and movement in zone concepts'],
  'speed-rusher': ['Speed Rusher', 'Threatens the edge with first-step burst'],
  'power-rusher': ['Power Rusher', 'Compresses the pocket with strength'],
  'run-setter': ['Run Setter', 'Controls the edge before pursuing the ball'],
  'high-motor': ['High Motor', 'Creates late-play pressure through relentless pursuit'],
  'hybrid-edge': ['Hybrid Edge', 'Balances rush, contain, and coverage responsibilities'],
  'nose-anchor': ['Nose Anchor', 'Occupies blocks and closes interior lanes'],
  'gap-penetrator': ['Gap Penetrator', 'Disrupts plays by winning immediately'],
  'interior-rusher': ['Interior Rusher', 'Generates passing-down pressure inside'],
  'two-gap-controller': ['Two-Gap Controller', 'Reads and controls multiple run lanes'],
  'pursuit-tackle': ['Pursuit Tackle', 'Closes space laterally and finishes outside the box'],
  'run-thumper': ['Run Thumper', 'Attacks downhill against the run'],
  'coverage-linebacker': ['Coverage Linebacker', 'Matches backs and tight ends in space'],
  'sideline-to-sideline': ['Sideline to Sideline', 'Uses range to erase space'],
  'blitz-specialist': ['Blitz Specialist', 'Times pressure and attacks protection rules'],
  'defensive-field-general': ['Defensive Field General', 'Organizes fits and coverage checks'],
  'press-corner': ['Press Corner', 'Disrupts releases with physical coverage'],
  'mirror-corner': ['Mirror Corner', 'Stays connected through route breaks'],
  'shutdown-corner': ['Shutdown Corner', 'Erases priority targets'],
  'zone-specialist': ['Zone Specialist', 'Reads route combinations and quarterbacks'],
  'ball-hawk': ['Ball Hawk', 'Trades some conservatism for takeaway chances'],
  'center-fielder': ['Center Fielder', 'Protects deep space with range'],
  'box-safety': ['Box Safety', 'Adds force near the line of scrimmage'],
  robber: ['Robber', 'Jumps intermediate routes from disguise'],
  enforcer: ['Enforcer', 'Finishes plays with physicality'],
  'coverage-safety': ['Coverage Safety', 'Matches receivers and limits explosives'],
  'accuracy-kicker': ['Accuracy Kicker', 'Prioritizes repeatable placement'],
  'power-kicker': ['Power Kicker', 'Extends field-goal and kickoff range'],
  'clutch-kicker': ['Clutch Kicker', 'Maintains composure in high-leverage attempts'],
  'kickoff-specialist': ['Kickoff Specialist', 'Creates distance and hang time on kickoffs'],
  'weather-kicker': ['Weather Kicker', 'Maintains repeatable mechanics in poor conditions'],
  'power-punter': ['Power Punter', 'Maximizes gross distance'],
  'coffin-corner': ['Coffin Corner', 'Trades distance for placement near the goal line'],
  'hang-time-specialist': ['Hang-Time Specialist', 'Helps coverage arrive before the return'],
  'directional-punter': ['Directional Punter', 'Limits returns through controlled placement'],
  'rugby-punter': ['Rugby Punter', 'Changes launch point and timing to stress return units'],
} as const;

export type PlayerPlayStyle = keyof typeof PLAYER_PLAY_STYLE_DEFINITIONS;
export type PlayerPlayStyleOption = { id: PlayerPlayStyle; label: string; description: string };

const BY_POSITION: Record<FranchisePlayerPosition, readonly PlayerPlayStyle[]> = {
  QB: ['pocket-passer', 'gunslinger', 'field-general', 'mobile', 'improviser'],
  RB: ['bell-cow', 'power-back', 'scat-back', 'receiving-back', 'blocking-back'],
  FB: ['blocking-back', 'power-back', 'receiving-back', 'h-back', 'short-yardage-back'],
  WR: ['route-technician', 'vertical-threat', 'slot-separator', 'possession-receiver', 'yac-creator'],
  TE: ['receiving-tight-end', 'inline-blocker', 'seam-threat', 'safety-valve', 'h-back'],
  OT: ['anchor', 'mauler', 'technician', 'athletic-protector', 'pulling-specialist'],
  OG: ['anchor', 'mauler', 'technician', 'pulling-specialist', 'zone-mover'],
  C: ['anchor', 'technician', 'line-caller', 'mauler', 'zone-mover'],
  DE: ['speed-rusher', 'power-rusher', 'run-setter', 'high-motor', 'hybrid-edge'],
  DT: ['nose-anchor', 'gap-penetrator', 'interior-rusher', 'two-gap-controller', 'pursuit-tackle'],
  LB: ['run-thumper', 'coverage-linebacker', 'sideline-to-sideline', 'blitz-specialist', 'defensive-field-general'],
  CB: ['press-corner', 'mirror-corner', 'shutdown-corner', 'zone-specialist', 'ball-hawk'],
  S: ['center-fielder', 'box-safety', 'robber', 'enforcer', 'coverage-safety'],
  K: ['accuracy-kicker', 'power-kicker', 'clutch-kicker', 'kickoff-specialist', 'weather-kicker'],
  P: ['power-punter', 'coffin-corner', 'hang-time-specialist', 'directional-punter', 'rugby-punter'],
};

function stableHash(value: string): number {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

export function playerPlayStyleOptions(position: FranchisePlayerPosition): PlayerPlayStyleOption[] {
  return BY_POSITION[position].map((id) => ({
    id,
    label: PLAYER_PLAY_STYLE_DEFINITIONS[id][0],
    description: PLAYER_PLAY_STYLE_DEFINITIONS[id][1],
  }));
}

export function isPlayerPlayStyleForPosition(style: unknown, position: FranchisePlayerPosition): style is PlayerPlayStyle {
  return typeof style === 'string' && BY_POSITION[position].includes(style as PlayerPlayStyle);
}

export function defaultPlayerPlayStyle(playerId: string, position: FranchisePlayerPosition): PlayerPlayStyle {
  const options = BY_POSITION[position];
  return options[stableHash(`${playerId}:${position}`) % options.length];
}

export function resolvePlayerPlayStyle(player: Pick<FranchisePlayer, 'id' | 'position' | 'playStyle'>): PlayerPlayStyle {
  return isPlayerPlayStyleForPosition(player.playStyle, player.position)
    ? player.playStyle
    : defaultPlayerPlayStyle(player.id, player.position);
}

export function playerPlayStyleLabel(style: PlayerPlayStyle): string {
  return PLAYER_PLAY_STYLE_DEFINITIONS[style][0];
}

export function playerPlayStyleDescription(style: PlayerPlayStyle): string {
  return PLAYER_PLAY_STYLE_DEFINITIONS[style][1];
}
